/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

/* eslint-disable lines-between-class-members */
import { generatePath, matchPath } from 'react-router-dom';
import { ExtraRoles, Roles } from './Roles';

const ALL_ROLES = Object.values(Roles);

/*
 * Enumeration of routes used in the application
 */
export class RoutePath {
  static DASHBOARD = new RoutePath('/', ALL_ROLES);

  static PROCEDURE_SEARCH = new RoutePath('/:procedure/:initialSubmissionProcedure?/search', ALL_ROLES);
  static PROCEDURE_DRAFT = new RoutePath('/:procedure/:initialSubmissionProcedure?/draft', [Roles.SUBMISSION_DRAFTER]);
  static PROCEDURE_EDIT = new RoutePath('/:procedure/draft/:submissionId/edit', [Roles.SUBMISSION_DRAFTER], true);
  static PROCEDURE_PAYMENT = new RoutePath('/:procedure/draft/:submissionId/payment', [Roles.SUBMISSION_PAYER], true);
  static PROCEDURE_VIEW = new RoutePath('/:procedure/draft/:submissionId/view', ALL_ROLES, true);
  static PROCEDURE_PREVIEW_SUBMISSION = new RoutePath('/:procedure/preview/:submissionId', ALL_ROLES, true);
  static PROCEDURE_SIGNING_SUBMISSION = new RoutePath(
    '/:procedure/signing/:submissionId',
    [Roles.SUBMISSION_SIGNER],
    true,
  );
  static PROCEDURE_NON_ORG_PREVIEW_SUBMISSION = new RoutePath(
    '/:procedure/preview/uma/:submissionId',
    [ExtraRoles.SUBMISSION_SIGN],
    true,
  );
  static PROCEDURE_NON_ORG_SIGNING_SUBMISSION = new RoutePath(
    '/:procedure/signing/uma/:submissionId',
    [ExtraRoles.SUBMISSION_SIGN],
    true,
  );
  static PROCEDURE_RECEIPTS_SUBMISSION = new RoutePath('/:procedure/receipts/:submissionId', ALL_ROLES);
  static PROCEDURE_REQUEST_RECEIPT_SUBMISSION = new RoutePath('/:procedure/receipts/:submissionId/request', [Roles.SUBMISSION_SENDER]);

  static EPCT_FILING = new RoutePath('/:procedure/:initialSubmissionProcedure?/epct/online-filing', ALL_ROLES);
  static EPCT_SUMMARY_SUBMISSION = new RoutePath('/:procedure/epct/summary/:submissionId/', ALL_ROLES, true);
  static EPCT_EDIT_SUBMISSION = new RoutePath('/:procedure/epct/edit/:submissionId/', ALL_ROLES, true);

  static EP_FILING = new RoutePath('/:procedure/ep/online-filing', ALL_ROLES);
  static EP_SUMMARY_SUBMISSION = new RoutePath('/:procedure/ep/summary/:submissionId/', ALL_ROLES, true);
  static EP_EDIT_SUBMISSION = new RoutePath('/:procedure/ep/edit/:submissionId/', ALL_ROLES, true);
  static EP1001_SIGNING = new RoutePath('/EP1001/signing/:submissionId');

  static ADDRESSBOOK_SEARCH = new RoutePath('/addressbook/search', ALL_ROLES);
  static ADDRESSBOOK_CONTACT = new RoutePath('/addressbook/contact', ALL_ROLES);
  static ADDRESSBOOK_CONTACT_ID = new RoutePath('/addressbook/contact/:contactId', ALL_ROLES);
  static ADDRESSBOOK_CONTACT_ID_EDIT = new RoutePath('/addressbook/contact/:contactId/edit', ALL_ROLES);

  // TODO: align all user / users paths to start with the same prefix and then change the order in App.jsx switch
  static USER_SEARCH = new RoutePath('/:organization/users/search', ALL_ROLES);
  static USER_PROFILE = new RoutePath('/user-profile/edit/:userId', ALL_ROLES);
  static USER_PROFILE_EDIT_ROLE = new RoutePath('/user-profile/edit/:userId/edit-roles/:currentOrgId', ALL_ROLES);
  static USER_PROFILE_VIEW = new RoutePath('/user-profile/view/:userId/view-profile/:currentOrgId', ALL_ROLES);
  static ORGANIZATION_CREATE = new RoutePath('/organisation/create', ALL_ROLES);
  static ORGANIZATION_EDIT = new RoutePath('/organisation/edit/:organisationId', ALL_ROLES);

  static SWITCH_ACCOUNT = new RoutePath('/switch-account');

  static USER_EMAIL_ACCEPT_INVITATION = new RoutePath('/user/:invitationType/:invitationId');

  static ACCESSIBILITY_STATEMENT = new RoutePath('/accessibility-statement');

  static NOT_FOUND = new RoutePath('/404');

  /**
   * Generate a path based on input parameters
   *
   * Example usage:
   *  RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, {procedure: 'NATPAT'})
   *
   * @param routePath - a RoutePath from above (e.g. RoutePath.PROCEDURE_SEARCH)
   * @param params - Path parameters to be replaced in the RoutePath (e.g. {procedure: 'EP1001EK'})
   * @returns {string} - newly formed path (e.g. '/NATPAT/search')
   */
  static generateRoutePath = (routePath, params) => generatePath(routePath.path, params);

  static getRoutePath = path =>
    Object.values(RoutePath).find(routePath => !!matchPath(path, routePath?.path)?.isExact) ?? {};

  /**
   * Checks if the user is restricted to access a submission outside his organization
   * Filters all RoutePaths that contain '/uma' string in the path & checks if the _path is part of these '/uma' paths
   * @param _path optional path param: will be checked if passed, otherwise window.location.pathname is checked
   * @returns {boolean}
   */
  static isUmaRoute = _path =>
    Object.values(RoutePath)
      .filter(routePath => routePath.path?.includes('/uma')) // all restricted routes
      .map(({ path }) => path)
      .some(path => matchPath(_path ?? window.location.pathname, path)?.isExact);

  // Class components
  constructor(path, roles = [], shouldLockSubmission = false) {
    this.path = path;
    this.roles = roles;
    this.shouldLockSubmission = shouldLockSubmission;
  }

  toString() {
    return `${this.path}`;
  }
}
